import SignUpForm from './SignUpForm';
import Testimony from './Testimony';
import useText from '@/hooks/useText';
import SharedBadge from '@/global/Badge/Badge';

function ApplyAcademic() {
  const showSignupInfo = process.env.REACT_APP_SHOW_SIGNUP_INFO === 'true';

  const testimonialsLabel = useText('home.testimonials.label');
  const testimonialsTitle = useText('home.testimonials.title');
  const eyebrow = useText('signup.apply_academic.label');
  const title = useText('signup.apply_academic.title');
  const titleHighlight = useText('signup.apply_academic.title_highlight');
  const titleSuffix = useText('signup.apply_academic.title_suffix');

  const partnerLogos = [
    { src: '/static/images/partners/Alexion-Logo.png', alt: 'Alexion logo', className: 'w-full h-full' },
    {
      src: '/static/images/partners/KyowaKirin_BrandLogo_colorv2-PhotoRoom-1-1024x216.png',
      alt: 'Kyowa Kirin logo',
      className: 'w-full h-full',
    },
    { src: '/static/images/partners/cerevel-logo.png', alt: 'Cerevel logo', className: 'w-full h-full' },
    {
      src: '/static/images/partners/company-servier.png',
      alt: 'Servier logo',
      className: 'w-full h-full scale-125',
    },
  ];
  const resultHighlights = [
    'Faster exploration of gene-disease-drug relationships',
    'Easier integration across research and computational pipelines',
    'Clear visibility into genetic evidence and therapeutic relevance',
  ];
  const summaryCards = [
    {
      title: '25+ authoritative',
      body: 'sources integrated',
    },
    {
      title: 'Ontology-aligned',
      body: 'semantic schema',
    },
    {
      title: 'Full provenance',
      body: 'evidence scoring',
    },
  ];

  return (
    <div className="apply-trial-content">
      <div className="md:grid md:grid-cols-2 md:gap-10 md:items-start">
        <div className="apply-trial-hero-col-height flex items-start justify-start px-4 md:px-0">
          <div className="w-full text-start">
            <div className="w-full hidden md:block ml-16 mb-8 mt-16">
              <SharedBadge text={eyebrow} icon={null} variant="secondary" size="sm" />
              <h6 className="text-start font-semibold text-4xl max-w-3xl">
                {title} <span className="home-hero-highlight">{titleHighlight}</span> {titleSuffix}
              </h6>
            </div>

            <div className="w-full flex justify-center mt-0 md:mt-0">
              <SignUpForm />
            </div>
          </div>
        </div>

        <div className="block apply-trial-hero-col-height px-4 mt-8 md:mt-0 md:px-2 md:overflow-auto">
          {showSignupInfo ? (
            <div className="h-full rounded-3xl bg-white text-slate-900 py-6 px-6 md:px-10 lg:p-8 space-y-6 shadow-sm border border-slate-200">
              <div>
                <h6 className="text-2xl md:text-4xl font-semibold tracking-wide mb-6 md:mb-8 text-blue">
                  Access structured translational knowledge layer
                </h6>
                <p className="text-sm leading-relaxed text-blue font-medium">
                  DISGENET transforms fragmented biomedical evidence into structured, traceable, and
                  actionable knowledge for discovery and therapeutic innovation.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 bg-gray-400 mb-3" style={{ height: '0.06rem' }}></div>
                  <p className="font-semibold text-gray-700 text-center whitespace-nowrap">
                    Used by industry leaders worldwide
                  </p>
                  <div className="flex-1 bg-gray-400 mb-3" style={{ height: '0.06rem' }}></div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-center mb-8">
                  {partnerLogos.map(logo => (
                    <div key={logo.src} className="rounded-md p-2 h-14 flex items-center">
                      <img src={logo.src} alt={logo.alt} className={`${logo.className} object-contain`} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h6 className="text-2xl font-semibold tracking-wide mb-4 text-blue">Why DISGENET?</h6>
                <p className="text-sm leading-relaxed text-blue font-medium">
                  DISGENET integrates information into structured, evidence-ranked genotype-phenotype-drug
                  relationships with full provenance, supported by tools for exploration, prioritization,
                  enrichment analysis, and programmatic integration.
                </p>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-4 mt-4 mb-8">
                  <div className="w-full sm:w-3/4 min-w-0">
                    <h3 className="text-lg font-semibold">The result</h3>
                    <ul className="space-y-2 text-sm text-slate-800 list-disc list-inside ml-4 mt-3">
                      {resultHighlights.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="w-full sm:w-1/4 flex flex-col items-center justify-center mt-1 sm:mt-3">
                    <div
                      className="w-20 h-20 rounded-full p-2.5 flex items-center justify-center shadow-md"
                      style={{
                        background: 'conic-gradient(from 220deg, #d02591, #52208f, #0a104f, #d02591)',
                      }}
                    >
                      <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                        <span className="text-lg font-bold text-blue">8,500</span>
                      </div>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-blue text-center whitespace-nowrap">
                      DISGENET citations
                    </p>
                  </div>
                </div>
              </div>

              <h6 className="font-semibold text-lg mt-4 text-blue">
                One infrastructure. All therapeutic areas. Trusted since 2010
              </h6>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {summaryCards.map(card => (
                  <div
                    key={card.title}
                    className="rounded-2xl border border-slate-300 bg-slate-50 px-3 pt-4 pb-2 text-center text-sm font-medium text-slate-800 leading-snug shadow-lg"
                  >
                    <p className="text-sm font-semibold mb-0 text-pink">{card.title}</p>
                    <p className="text-sm leading-relaxed text-blue font-medium">{card.body}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="hidden md:flex items-center justify-center md:overflow-hidden apply-trial-hero-col-height">
              <img
                src="/static/images/signup-banner.jpg"
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover object-bottom"
              />
            </div>
          )}
        </div>
      </div>

      <section id="testimonials" className="home-section home-section-dark home-testimonials">
        <div className="text-center">
          <span className="home-section-label text-pink mb-4">{testimonialsLabel}</span>
          <h2 className="home-section-title home-section-title-center home-section-title-light text-white">
            {testimonialsTitle}
          </h2>
        </div>
        <Testimony variant="cards" />
      </section>
    </div>
  );
}

export default ApplyAcademic;
